/**
 * Shrinks a camera photo before upload: long edge ~2000px, JPEG. Re-drawing on a canvas bakes in the
 * EXIF orientation and drops all metadata (including GPS). Falls back to the original file if the
 * browser can't decode it.
 */
export async function compressPhoto(file: File, maxEdge = 2000, quality = 0.82): Promise<File> {
  const draw = async (src: CanvasImageSource, w0: number, h0: number) => {
    const s = Math.min(1, maxEdge / Math.max(w0, h0));
    const w = Math.max(1, Math.round(w0 * s)), h = Math.max(1, Math.round(h0 * s));
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const g = c.getContext("2d"); if (!g) return null;
    g.drawImage(src, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, "image/jpeg", quality));
    c.width = c.height = 0; // free iOS canvas memory
    return blob && new File([blob], `${file.name.replace(/\.[^.]+$/, "") || "photo"}.jpg`, { type: "image/jpeg", lastModified: file.lastModified });
  };
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
    const out = await draw(bmp, bmp.width, bmp.height); bmp.close();
    if (out) return out;
  } catch { /* try an <img> (Safari decodes HEIC there) */ }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image(); img.src = url; await img.decode();
    return (await draw(img, img.naturalWidth, img.naturalHeight)) ?? file;
  } catch { return file; } finally { URL.revokeObjectURL(url); }
}
